import { useState } from "react";
import { Image as ImageIconLucide, UploadCloud, X } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";

/**
 * BrandModal — minimal but complete: upload (or replace) a logo, pick
 * a default corner, set default opacity, or remove the logo entirely.
 */
export default function BrandModal({ brand, onClose, onUpdate, onRemove }) {
  const [logo, setLogo] = useState(null);
  const [corner, setCorner] = useState(brand.corner || "br");
  const [opacity, setOpacity] = useState(brand.opacity ?? 0.7);

  const dirty = !!logo || corner !== brand.corner || Math.abs((opacity ?? 0.7) - (brand.opacity ?? 0.7)) > 0.001;

  const save = async () => {
    await onUpdate({ logo, corner, opacity });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="catalog-brand-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0E0E0E] p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Brand assets</div>
            <h3 className="font-display text-lg text-white mt-1">Your watermark</h3>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white" data-testid="catalog-brand-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          {brand.has_logo && !logo && (
            <div className="rounded-lg border border-white/10 bg-[#141414] p-3 flex items-center gap-3" data-testid="catalog-brand-current-logo">
              <img src={brand.logo_url ? `${getBackendUrl()}${brand.logo_url}` : ""} alt="Brand logo" className="w-12 h-12 object-contain bg-white rounded" />
              <div className="text-xs text-white/65">
                <div>Current logo</div>
                <button onClick={onRemove} data-testid="catalog-brand-remove-btn" className="text-rose-300 hover:text-rose-200 underline-offset-2 hover:underline mt-1 text-[11px]">
                  Remove
                </button>
              </div>
            </div>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">
              {brand.has_logo ? "Replace logo (PNG with transparency works best)" : "Upload logo (PNG with transparency works best)"}
            </span>
            <div className="relative flex items-center justify-center w-full rounded-lg border-2 border-dashed border-white/10 hover:border-white/30 bg-[#0A0A0A] cursor-pointer" style={{ minHeight: 90 }} data-testid="catalog-brand-upload">
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogo(e.target.files?.[0] || null)} className="absolute inset-0 opacity-0 cursor-pointer" />
              {logo ? (
                <div className="flex items-center gap-3 px-4 py-3">
                  <ImageIconLucide className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs text-white/85 truncate max-w-[20ch]">{logo.name}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 px-4 py-3">
                  <UploadCloud className="w-5 h-5 text-white/55" />
                  <span className="text-[11px] text-white/55">Drop or click</span>
                </div>
              )}
            </div>
          </label>

          <div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Default corner</span>
            <div className="grid grid-cols-4 gap-1.5" data-testid="catalog-brand-corner">
              {[
                { v: "tl", label: "Top L" },
                { v: "tr", label: "Top R" },
                { v: "bl", label: "Bot L" },
                { v: "br", label: "Bot R" },
              ].map((c) => (
                <button
                  key={c.v}
                  type="button"
                  onClick={() => setCorner(c.v)}
                  data-testid={`catalog-brand-corner-${c.v}`}
                  className={`text-[11px] py-1.5 rounded-md border transition ${corner === c.v ? "border-[#D4AF37] bg-[#D4AF37]/15 text-white" : "border-white/10 bg-[#141414] text-white/65 hover:border-white/30"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">Default opacity</span>
              <span className="text-[11px] text-[#D4AF37] tabular-nums" data-testid="catalog-brand-opacity-value">{Math.round((opacity ?? 0.7) * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.05"
              value={opacity ?? 0.7}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              data-testid="catalog-brand-opacity-slider"
              className="w-full accent-[#D4AF37]"
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={!dirty && !logo}
            data-testid="catalog-brand-save-btn"
            className="flex-1 text-sm rounded-full bg-[#D4AF37] text-black py-2.5 hover:bg-[#F3CE5A] transition disabled:opacity-50"
          >
            Save brand
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-sm rounded-full border border-white/10 text-white/75 px-5 py-2.5 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
